import { useNavigate } from 'react-router-dom';
import Avatar from '@/components/base/Avatar';
import PlatformIcon from '@/components/base/PlatformIcon';
import { formatCompact } from '@/utils/format';
import type { CreatorProfile } from '@/types/portal';
import { ReachBar } from './CreatorVisuals';
import { engagementVerdict, readiness } from './creatorInsights';

interface CreatorGridCardProps {
  creator: CreatorProfile;
  rank: number;
  inCompare: boolean;
  saved: boolean;
  onToggleCompare: () => void;
  onToggleSave: () => void;
  inCampaign?: boolean;
  onAddToCampaign?: () => void;
}

/** Fit score colour: green from 75, amber from 50, burgundy below, grey with no brief. */
function fitTone(score: number | null) {
  if (score === null) return 'bg-background-200 text-foreground-600';
  if (score >= 75) return 'bg-accent-500 text-background-50';
  if (score >= 50) return 'bg-secondary-400 text-background-50';
  return 'bg-primary-500 text-background-50';
}

/**
 * A compact creator tile for the grid view: built for scanning many creators. The same actions as
 * the list card, as icon buttons; the whole tile opens the profile.
 */
export default function CreatorGridCard({
  creator,
  rank,
  inCompare,
  saved,
  onToggleCompare,
  onToggleSave,
  inCampaign = false,
  onAddToCampaign,
}: CreatorGridCardProps) {
  const navigate = useNavigate();
  const ready = readiness(creator);
  const engagement = engagementVerdict(creator.engagementRate);
  const openProfile = () => navigate(`/creators/${creator.id}`, { state: { from: 'discovery', creator } });
  const iconButton =
    'inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors cursor-pointer disabled:cursor-default';

  return (
    <article
      className="creator-card group flex flex-col rounded-xl border border-background-200 bg-background-50 p-4"
      style={{ animationDelay: `${Math.min(rank - 1, 12) * 45}ms` }}
    >
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-label font-semibold text-foreground-400">#{rank}</span>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${fitTone(creator.fitScore)}`}
          title={creator.fitScore === null ? 'Describe your brief to get a fit score' : `Fit score ${creator.fitScore} out of 100`}
        >
          {creator.fitScore ?? '—'} fit
        </span>
      </div>

      <button type="button" onClick={openProfile} className="mt-1 flex cursor-pointer flex-col items-center text-center">
        <span className="relative rounded-full ring-2 ring-transparent ring-offset-2 transition-[box-shadow] duration-300 group-hover:ring-primary-300">
          <Avatar src={creator.avatar} alt={creator.name} size="xl" />
          <span
            className={`absolute bottom-1 right-1 h-4 w-4 rounded-full border-2 border-background-50 ${ready.ready ? 'bg-accent-500' : 'bg-secondary-400'}`}
            title={ready.label}
          />
        </span>
        <span className="mt-2.5 flex items-center gap-1 font-heading text-sm font-semibold text-foreground-950">
          {creator.name}
          {creator.verification === 'Verified' ? (
            <i className="ri-verified-badge-fill text-[14px] text-accent-600" aria-label="Verified" />
          ) : null}
        </span>
        <span className="mt-0.5 truncate text-xs text-foreground-500">{creator.city || creator.handle}</span>
        {creator.categories.length > 0 ? (
          <span className="mt-1.5 text-[11px] font-medium text-secondary-800">{creator.categories.slice(0, 2).join(' · ')}</span>
        ) : null}
      </button>

      <dl className="mt-3 grid grid-cols-3 gap-1 rounded-lg bg-background-100 px-2 py-2 text-center">
        <div>
          <dt className="text-[10px] text-foreground-400">Followers</dt>
          <dd className="font-heading text-[13px] font-semibold text-foreground-900">{formatCompact(creator.followers)}</dd>
        </div>
        <div>
          <dt className="text-[10px] text-foreground-400">Engage</dt>
          <dd className="font-heading text-[13px] font-semibold text-foreground-900">{creator.engagementRate}%</dd>
          <dd className={`text-[10px] font-semibold ${engagement.tone}`}>{engagement.label}</dd>
        </div>
        <div>
          <dt className="text-[10px] text-foreground-400">Rate</dt>
          <dd className="truncate font-heading text-[13px] font-semibold text-foreground-900">
            {creator.rate != null ? `${creator.currency} ${formatCompact(creator.rate).replace(/\.0(?=[KMB])/, '')}` : '—'}
          </dd>
        </div>
      </dl>

      <ReachBar platforms={creator.platforms} className="mt-2.5" />

      <div className="mt-2.5 flex justify-center gap-2 text-base text-foreground-500">
        {creator.platforms.map((account) => (
          <span
            key={`${account.platform}-${account.handle}`}
            title={`${account.platform} · ${formatCompact(account.followers)} · ${account.engagementRate}%`}
          >
            <PlatformIcon platform={account.platform} />
          </span>
        ))}
      </div>

      <div className="mt-auto flex items-center justify-between gap-1.5 border-t border-background-100 pt-3">
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={onToggleSave}
            aria-label={saved ? 'Remove from saved' : 'Save creator'}
            title={saved ? 'Saved' : 'Save'}
            className={`${iconButton} ${saved ? 'border-secondary-400 bg-secondary-100 text-secondary-900' : 'border-background-300 text-foreground-500 hover:bg-background-100'}`}
          >
            <i className={saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
          </button>
          <button
            type="button"
            onClick={onToggleCompare}
            aria-label={inCompare ? 'Remove from compare' : 'Compare'}
            title={inCompare ? 'In compare' : 'Compare'}
            className={`${iconButton} ${inCompare ? 'border-secondary-400 bg-secondary-100 text-secondary-900' : 'border-background-300 text-foreground-500 hover:bg-background-100'}`}
          >
            <i className={inCompare ? 'ri-check-line' : 'ri-scales-3-line'} />
          </button>
        </div>
        {onAddToCampaign ? (
          <button
            type="button"
            onClick={onAddToCampaign}
            disabled={inCampaign}
            className={`inline-flex h-8 items-center gap-1 rounded-md px-2.5 text-xs font-medium transition-colors cursor-pointer disabled:cursor-default ${
              inCampaign ? 'bg-accent-100 text-accent-800' : 'bg-accent-500 text-background-50 hover:bg-accent-600'
            }`}
          >
            <i className={inCampaign ? 'ri-check-line' : 'ri-user-add-line'} aria-hidden="true" />
            {inCampaign ? 'Added' : 'Add'}
          </button>
        ) : (
          <button
            type="button"
            onClick={openProfile}
            className="inline-flex h-8 items-center gap-1 rounded-md bg-primary-500 px-2.5 text-xs font-medium text-background-50 hover:bg-primary-600 cursor-pointer"
          >
            Profile
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </button>
        )}
      </div>
    </article>
  );
}
